import {createBatchAtomicPayLoad} from "@/utilities/BatchSaveUtils.ts";
import {getConstraintViolation, processServerError} from "@/services/utilities/ServiceError.ts";
import {NO_CONTENT} from "@/constants/response-status-codes.ts";
import {isSuccessStatus} from "@/utilities/requestResponseValidator.ts";
import {genericError} from "@/models/GenericError.ts";
import {APIConnector} from "@/services/api/APIConnector.ts";
import {getMasterGroupRelationshipsBatch} from "@/services/accessGroupService.ts";

const apiConnector = new APIConnector();

/*
This whole class is for using the batching system within Elide, this is to make sure we don't have to make either multiple calls to the backend from the frontend or have to create a special endpoint.

The batching system in Elide is called Atomic Payload.

It will allow the users to send multiple operation calls (add, remove, update) in a single Elide request.

You can mix and match operations in a single call.

In each operation call, you have to have the following details:
- op: This is the type of operation you want to do, either add, remove or update.
- lid (local id) that is used by Elide to track new records but is not used by Hibernate for the persist and any attributes or relationships that are required for that relationship.

 */
const handleBatchResponse = (response: Response): any => {
    return new Promise<any>(async (resolve, reject) => {
        if (response.status === NO_CONTENT) {
            return resolve({ id: 1, success: true });
        }

        const json = await response.json();

        if (json.errors) {
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        }
        else if (json.length > 0) {
            const rawErrors: string = json.map((error) => {
                if (error['errors'][0].detail && !(error['errors'][0].detail.includes('Subsequent operation failed') || error['errors'][0].detail.includes('Terminated by earlier failure'))) {
                    return error['errors'][0].detail;
                }
            }).join(';');
            return reject({ status: response.status, id: 1, success: false, message: rawErrors || genericError });
        }

        if (isSuccessStatus(response.status)) {
            return resolve({ id: 1, success: true, data: json });
        }

        return reject({ status: response.status, id: 1, success: false, message: genericError });
    });
};

export const batchAssignUnassignCompanyDepartmentRelationship = async (toBeAssigned: string[], toBeUnassigned: string[], itemId: string, entity: "company" | "department") => {
    const addOperations = toBeAssigned.map((masterId, index) => ({
        op: "add",
        ref: {
            type: "master",
            id: masterId,
            relationship: entity,
        },
        data: [
            { type: entity, id: itemId, lid: `new-mg-${index}` }
        ]
    }));

    const removeOperations = toBeUnassigned.map((masterId, index) => ({
        op: "remove",
        ref: {
            type: "master",
            id: masterId,
            relationship: entity,
        },
        data: [
            { type: entity, id: itemId, lid: `new-mg-${index}` }
        ]
    }));

    try {
        let response: Response;
        return await apiConnector.sendAtomicBatchDataWithHeaders(createBatchAtomicPayLoad(addOperations, [], removeOperations)).then(data => {
            response = data;
            return Promise.resolve(handleBatchResponse(response));
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const batchAssignUnassignMasterGroupRelationship = async (toBeAssigned: string[], toBeUnassigned: string[], accessGroupId: string): Promise<any> => {
    const addOperations = toBeAssigned.map((masterId, index) => ({
        op: "add",
        data: {
            type: "masterGroup",
            lid: `new-mg-${index}`,
            attributes: {
                mgExpires: 0,
            },
            relationships: {
                master: { data: { type: "master", id: masterId } },
                accessGroup: { data: { type: "accessGroup", id: accessGroupId } }
            }
        }
    }));

    // If there is unassigned masterIds then find the ones that exist (precautionary step) to make sure we don't try and delete something that
    // doesn't exist.
    const unassignRelationships = toBeUnassigned.length > 0
        ? await getMasterGroupRelationshipsBatch(toBeUnassigned, accessGroupId)
        : [];

    console.log("Unassign Relationships: ", unassignRelationships);
    // Delete Master groups (not breakup relations or edit data)
    const removeOperations = unassignRelationships.map(id => ({
        op: "remove",
        ref: {
            type: "masterGroup",
            id: id
        }
    }));

    try {
        let response: Response;
        return await apiConnector.sendAtomicBatchDataWithHeaders(createBatchAtomicPayLoad(addOperations, [], removeOperations)).then(data => {
            response = data;
            return Promise.resolve(handleBatchResponse(response));
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });
    }
    catch (error) {
        return Promise.reject(processServerError(error));
    }
}
